import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { PolicyKey } from '@kirin/api-client/contracts/policies';
import { Card, CardContent } from '@/components/ui/card';
import { Heading1 } from '@/components/shared/typography/Heading';
import { BodyMuted } from '@/components/shared/typography/BodyText';
import { BuiltInCatalogPanel } from './BuiltInCatalogPanel';

interface BuiltInCatalogPageProps {
  /** Path back to the Policies list. */
  policiesPath: string;
}

/**
 * KIR-2691: the read-only "Built-in Detection Catalog" page
 * (Policies > Extension Security > Built-in Detection Catalog).
 *
 * Shows the full weighted-scoring catalog grouped by tier. Purely
 * informational — every rule is locked; customization lives in the separate
 * "Custom Capability Rules" editor on the policy card.
 */
export function BuiltInCatalogPage({
  policiesPath,
}: BuiltInCatalogPageProps): ReactElement {
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Link
          to={`${policiesPath}?policy=${PolicyKey.ExtensionSecurity}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
          Policies
        </Link>
        <Heading1>Built-in Detection Catalog</Heading1>
        <BodyMuted>
          Read-only reference of the rules Kirin evaluates against extension
          manifests when capability detection is enabled. To add your own
          signals, use the Custom Capability Rules editor on the Extension
          Security policy.
        </BodyMuted>
      </div>

      <Card>
        <CardContent className="pt-6">
          <BuiltInCatalogPanel />
        </CardContent>
      </Card>
    </div>
  );
}
